import React, { useEffect, useRef, useState } from 'react';
import { Navigate, useLocation, useParams } from 'react-router-dom';
import { ArrowRightIcon, BadgeCheckIcon, CheckIcon, ClockIcon, DollarSignIcon, GraduationCapIcon, LayersIcon, MonitorIcon, ShieldCheckIcon, UserIcon } from 'lucide-react';
import { useMetaTags } from '../hooks/useMetaTags';
import { useMetaPixel } from '../hooks/useMetaPixel';
import { getProgramInformationPage, resolveProgramInformationCodeSlug } from '../data/programInformationPages';
import { ProgramMatchForm } from '../components/programInformation/ProgramMatchForm';
import { SchoolMatchCarousel } from '../components/programInformation/SchoolMatchCarousel';
import { TrackingNoticeLinks } from '../components/TrackingNoticeLinks';
import type { LeadAnswers, LeadContext } from '../utils/mediaMatchersApi';
const BENEFIT_ICONS = [ShieldCheckIcon, ClockIcon, DollarSignIcon];
export function ProgramInformationPage() {
  const {
    programSlug
  } = useParams();
  const location = useLocation();
  const formRef = useRef<HTMLDivElement>(null);
  const [submitted, setSubmitted] = useState<{
    answers: LeadAnswers;
    context: LeadContext;
  } | null>(null);
  useMetaPixel();

  // The explicit route in App.tsx carries no param, so fall back to the path.
  const slugFromPath = location.pathname.replace(/^\/program-information\/?/, '').replace(/\/+$/, '');
  const rawSlug = (programSlug || slugFromPath || '').toLowerCase();
  const codeSlug = resolveProgramInformationCodeSlug(rawSlug);
  const page = getProgramInformationPage(codeSlug || rawSlug);
  useMetaTags({
    title: page?.metaTitle || 'Program Information | FindOnlineDegrees',
    description: page?.metaDescription || 'Compare accredited online degree programs and get matched with schools enrolling now.',
    robots: 'noindex, follow'
  });
  useEffect(() => {
    window.scrollTo(0, 0);
  }, [submitted]);

  // Numeric shorthand (/program-information/3-101) redirects to the readable
  // slug so that is what gets shared and reported.
  if (codeSlug && codeSlug !== rawSlug) {
    return <Navigate to={`/program-information/${codeSlug}${location.search}`} replace data-id="element-2335" data-mp="25hvrmp9hidwg" />;
  }

  // An unknown combination lands on the browsable directory rather than a
  // dead end.
  if (!page) {
    return <Navigate to="/program-information" replace data-id="element-2336" data-mp="2f7xitpx4wcfl" />;
  }
  if (submitted) {
    return <div className="bg-gray-50" data-id="element-2337" data-mp="1hm0505tp8v9z">
        <SchoolMatchCarousel answers={submitted.answers} context={submitted.context} programLabel={page.programLabel} exitTo="/program-information" data-id="element-2338" data-mp="1hmt18cv182dv" />
        <footer className="border-t border-gray-200 bg-white px-4 py-8 text-center text-xs" data-id="element-2339" data-mp="1rkz1kq16x0gm">
          <TrackingNoticeLinks tone="light" data-id="element-2340" data-mp="1wfw9tuqnb5sg" />
        </footer>
      </div>;
  }
  const scrollToForm = () => {
    formRef.current?.scrollIntoView({
      behavior: 'smooth',
      block: 'center'
    });
  };
  const glance = [{
    label: 'Credential',
    value: page.degreeShort
  }, {
    label: 'Program',
    value: page.programLabel
  }, {
    label: 'Field',
    value: page.areaLabel
  }, {
    label: 'Format',
    value: 'Online'
  }];
  const facts = [{
    icon: GraduationCapIcon,
    label: 'Credential',
    value: page.degreeShort
  }, {
    icon: LayersIcon,
    label: 'Field',
    value: page.areaLabel
  }, {
    icon: MonitorIcon,
    label: 'Format',
    value: 'Online'
  }, {
    icon: ClockIcon,
    label: 'Start dates',
    value: 'Year-round'
  }];
  return <div className="bg-white" data-id="element-2341" data-mp="8k80wwsahulx">
      {/* Hero — the form is the page's primary action, so it sits beside the
          headline and stays in view while the visitor scrolls the content. */}
      <section className="relative isolate overflow-hidden bg-[#17316f]" data-id="element-2342" data-mp="3myb3g0zngxk">
        <img src={page.heroImage} alt={page.heroImageAlt} className="absolute inset-0 h-full w-full object-cover object-[50%_20%]" data-id="element-2343" data-mp="3ut98n26shzk5" />
        <div className="absolute inset-0 bg-[#17316f]/85" aria-hidden="true" data-id="element-2344" data-mp="1t137w77eimkq" />
        <div className="relative mx-auto grid max-w-6xl gap-10 px-4 py-12 md:py-16 lg:grid-cols-[1fr_400px] lg:items-start" data-id="element-2345" data-mp="qnym19sbkrf1">
          <div data-id="element-2346" data-mp="unfp5wk9xch1">
            <nav aria-label="Breadcrumb" className="text-sm text-blue-200" data-id="element-2347" data-mp="ulwagae9n9hd">
              <a href="/program-information" className="underline-offset-2 hover:underline" data-id="element-2348" data-mp="go7qr5vxq8dw">
                Program Information
              </a>
              <span className="px-2" aria-hidden="true" data-id="element-2349" data-mp="6xd2jvz4oypt">
                /
              </span>
              <span className="text-white" data-id="element-2350" data-mp="bqxqev70ccba">{page.areaLabel}</span>
            </nav>
            <p className="mt-5 inline-flex items-center gap-1.5 rounded-full border border-blue-300/50 px-3 py-1 text-xs font-bold uppercase tracking-wide text-blue-100" data-id="element-2351" data-mp="3u4nh9zmgcepe">
              <GraduationCapIcon className="h-3.5 w-3.5" aria-hidden="true" data-id="element-2352" data-mp="3k4o9w61rque4" />
              {page.degreeShort}
            </p>
            <h1 className="mt-4 text-3xl font-bold leading-tight text-white md:text-5xl" data-id="element-2353" data-mp="3p2y4ofjkzsz7">
              {page.headline}
            </h1>
            <p className="mt-6 max-w-xl text-lg leading-relaxed text-blue-100" data-id="element-2354" data-mp="35kk6ecl4w2xk">
              {page.subhead}
            </p>
            <ul className="mt-8 flex flex-wrap gap-x-6 gap-y-2 text-sm text-blue-50 lg:mt-16" data-id="element-2355" data-mp="3fgdr4j90b2qf">
              {['Accredited schools', 'Flexible online study', 'No obligation'].map(item => <li key={item} className="flex items-center gap-2" data-id="element-2356" data-mp="3ogtt1oa3q1t6">
                    <CheckIcon className="h-4 w-4 text-blue-200" aria-hidden="true" data-id="element-2357" data-mp="3kfqb8w8htvpb" />
                    {item}
                  </li>)}
            </ul>
          </div>
          <div ref={formRef} className="lg:sticky lg:top-8" data-id="element-2358" data-mp="3t8lhnt9p5xgo">
            <ProgramMatchForm degreeInterest={page.degreeInterest} areaStudy={page.areaId} mProgram={page.mProgram} onComplete={(answers, context) => setSubmitted({
            answers,
            context
          })} data-id="element-2359" data-mp="bzf1ltmsqm4k" />
          </div>
        </div>
      </section>

      {/* Facts strip — the four things someone scanning wants confirmed. */}
      <section className="border-b border-gray-200 bg-gray-50" data-id="element-2360" data-mp="o1y2ys1phpa8">
        <dl className="mx-auto grid max-w-6xl gap-6 px-4 py-6 sm:grid-cols-2 lg:grid-cols-4" data-id="element-2361" data-mp="ssy0nzingbds">
          {facts.map(fact => <div key={fact.label} className="flex items-center gap-3" data-id="element-2362" data-mp="xssr61yiiyyb">
              <fact.icon className="h-6 w-6 shrink-0 text-[#1d4ed8]" aria-hidden="true" data-id="element-2363" data-mp="11tmfgwfvafrq" />
              <div data-id="element-2364" data-mp="16tr0mzrpzyoa">
                <dt className="text-xs font-bold uppercase tracking-wide text-gray-500" data-id="element-2365" data-mp="16t8t36zlxy12">
                  {fact.label}
                </dt>
                <dd className="font-bold text-gray-900" data-id="element-2366" data-mp="38k2h8innvnko">{fact.value}</dd>
              </div>
            </div>)}
        </dl>
      </section>

      <div className="mx-auto grid max-w-6xl gap-12 px-4 py-12 md:py-16 lg:grid-cols-[1fr_320px]" data-id="element-2367" data-mp="3djym0k46nj1a">
        <main data-id="element-2368" data-mp="360frbglku1cv">
          <h2 className="text-2xl font-bold text-[#1e40af]" data-id="element-2369" data-mp="360z1gb4zjlgc">
            About This Program
          </h2>
          <div className="mt-4 space-y-4 text-[17px] leading-relaxed text-gray-700" data-id="element-2370" data-mp="316uu3qrt5beq">
            {page.aboutParagraphs.map(paragraph => <p key={paragraph} data-id="element-2371" data-mp="2rb9u1y6aul7m">{paragraph}</p>)}
          </div>

          <h2 className="mt-12 text-2xl font-bold text-[#1e40af]" data-id="element-2372" data-mp="3oyia3dnj7228">
            What to Compare Between Schools
          </h2>
          <ul className="mt-4 space-y-3" data-id="element-2373" data-mp="3jz5sp8ffin71">
            {page.compareItems.map(item => <li key={item} className="flex items-start gap-3 text-gray-700" data-id="element-2374" data-mp="3ezt2qet9h008">
                <CheckIcon className="mt-1 h-5 w-5 shrink-0 text-[#1d4ed8]" aria-hidden="true" data-id="element-2375" data-mp="3a19eu8v3xe58" />
                <span className="leading-relaxed" data-id="element-2376" data-mp="23lyv8m8if0w4">{item}</span>
              </li>)}
          </ul>

          <h2 className="mt-12 text-2xl font-bold text-[#1e40af]" data-id="element-2377" data-mp="1ykj9st2f6sd7">
            {page.whyHeading}
          </h2>
          <div className="mt-5 grid gap-8 sm:grid-cols-3" data-id="element-2378" data-mp="2ocb28k1vhb6g">
            {page.benefits.map((benefit, i) => {
            const Icon = BENEFIT_ICONS[i] || BadgeCheckIcon;
            return <div key={benefit.title} data-id="element-2379" data-mp="2fbmolqge5cu3" data-mpi={i}>
                  <Icon className="h-6 w-6 text-[#1d4ed8]" aria-hidden="true" data-id="element-2380" data-mp="2jalqt3aqmvhu" />
                  <h3 className="mt-3 font-bold text-[#1e40af]" data-id="element-2381" data-mp="3719aelu0n9k8">
                    {benefit.title}
                  </h3>
                  <p className="mt-2 text-sm leading-relaxed text-gray-600" data-id="element-2382" data-mp="3c1x3re5pw2hm">
                    {benefit.body}
                  </p>
                </div>;
          })}
          </div>
        </main>

        <aside className="space-y-6" data-id="element-2383" data-mp="3pzllwe8egviz">
          <div className="rounded-xl bg-gray-50 p-5" data-id="element-2384" data-mp="3pyiw69mtcz4n">
            <h2 className="text-xs font-bold uppercase tracking-wide text-gray-500" data-id="element-2385" data-mp="3pxr2j4cs1bh0">
              At a glance
            </h2>
            <dl className="mt-4 space-y-2.5 text-sm" data-id="element-2386" data-mp="1jatgln5fyte5">
              {glance.map(row => <div key={row.label} className="flex justify-between gap-4" data-id="element-2387" data-mp="1ecrusugmsfyz">
                  <dt className="text-gray-600" data-id="element-2388" data-mp="1t14t3y36yawn">{row.label}</dt>
                  <dd className="text-right font-bold text-gray-900" data-id="element-2389" data-mp="1t0lcqlt6vukq">
                    {row.value}
                  </dd>
                </div>)}
            </dl>
            <button type="button" onClick={scrollToForm} className="mt-5 w-full rounded-lg bg-[#e89b33] px-5 py-3 text-sm font-bold uppercase tracking-wide text-[#17255a] transition-colors hover:bg-[#d98c22] focus:outline-none focus-visible:ring-2 focus-visible:ring-[#1e40af] focus-visible:ring-offset-2" data-id="element-2390" data-mp="10die7toijjuh">
              Get Matched Now
            </button>
          </div>

          {page.otherLevels.length > 0 && <nav className="rounded-xl border border-gray-200 p-5" data-id="element-2391" data-mp="2gr8jv4va6g68">
              <h2 className="text-xs font-bold uppercase tracking-wide text-gray-500" data-id="element-2392" data-mp="2gt1sgppgfagv">
                Other levels
              </h2>
              <ul className="mt-3 divide-y divide-gray-100" data-id="element-2393" data-mp="2ofm6ij015nyg">
                {page.otherLevels.map(link => <li key={link.to} data-id="element-2394" data-mp="2jodtze3elmh2">
                    <a href={link.to} className="group flex items-center justify-between gap-3 py-3" data-id="element-2395" data-mp="2jncij8ih6xyt">
                      <span data-id="element-2396" data-mp="29s11uknibgof">
                        <span className="block font-semibold text-gray-900 group-hover:text-[#1e40af]" data-id="element-2397" data-mp="29p52crw0gjxv">
                          {link.label}
                        </span>
                        <span className="block text-xs text-gray-500" data-id="element-2398" data-mp="20rcntpy45gbj">
                          {link.sublabel}
                        </span>
                      </span>
                      <ArrowRightIcon className="h-4 w-4 shrink-0 text-gray-400 transition-colors group-hover:text-[#1e40af]" aria-hidden="true" data-id="element-2399" data-mp="3v4udpjlzqp9q" />
                    </a>
                  </li>)}
              </ul>
            </nav>}

          <div className="rounded-xl border border-gray-200 p-5" data-id="element-2400" data-mp="6hefy47g4nc2">
            <UserIcon className="h-5 w-5 text-[#1d4ed8]" aria-hidden="true" data-id="element-2401" data-mp="b8waa9eyrpo2" />
            <p className="mt-3 text-sm leading-relaxed text-gray-600" data-id="element-2402" data-mp="g8ivp64y4c7y">
              Not sure which program fits? Search every degree level and field
              in one place.
            </p>
            <a href="/program-information" className="mt-3 inline-flex items-center gap-1.5 text-sm font-bold text-[#1e40af] hover:underline" data-id="element-2403" data-mp="3jp97y2r4fli3">
              Browse all programs
              <ArrowRightIcon className="h-4 w-4" aria-hidden="true" data-id="element-2404" data-mp="3tnyfb1le5nk3" />
            </a>
          </div>
        </aside>
      </div>

      {page.siblings.length > 0 && <section className="border-t border-gray-200 bg-gray-50 py-12" data-id="element-2405" data-mp="1407mgi24csms">
          <div className="mx-auto max-w-6xl px-4" data-id="element-2406" data-mp="18vmu2s9j9sar">
            <h2 className="text-2xl font-bold text-[#1e40af]" data-id="element-2407" data-mp="3502qgb1trmdh">
              Other {page.areaLabel.toLowerCase()} programs
            </h2>
            <p className="mt-2 text-gray-600" data-id="element-2408" data-mp="300gc1ya9g033">
              Still deciding? These {page.degreeShort.toLowerCase()} programs sit
              in the same field.
            </p>
            <ul className="mt-5 flex flex-wrap gap-3" data-id="element-2409" data-mp="39z5w9xpmpwmi">
              {page.siblings.map(link => <li key={link.to} data-id="element-2410" data-mp="2m91p5gd8feqr">
                  <a href={link.to} className="inline-flex items-center gap-2 rounded-full border border-gray-200 bg-white px-4 py-2 text-sm font-semibold text-[#1e40af] transition-colors hover:border-[#1d4ed8]" data-id="element-2411" data-mp="2ma32xslmufc6">
                    {link.label}
                    <ArrowRightIcon className="h-4 w-4" aria-hidden="true" data-id="element-2412" data-mp="2q9bwawcrgb77" />
                  </a>
                </li>)}
            </ul>
          </div>
        </section>}

      <section className="bg-[#1e40af] py-14 text-center" data-id="element-2413" data-mp="1yk0fxqwkrzkf">
        <div className="mx-auto max-w-2xl px-4" data-id="element-2414" data-mp="1og22yd09juxi">
          <h2 className="text-3xl font-bold text-white" data-id="element-2415" data-mp="1oe8x2zxh5eqk">
            Ready to Take the Next Step?
          </h2>
          <p className="mt-4 leading-relaxed text-blue-100" data-id="element-2416" data-mp="1ei5vpwag5k4o">
            {page.closingBody}
          </p>
          <button type="button" onClick={scrollToForm} className="mt-8 inline-flex items-center gap-2 rounded-lg bg-[#e89b33] px-8 py-4 text-sm font-bold uppercase tracking-wide text-[#17255a] transition-colors hover:bg-[#d98c22] focus:outline-none focus-visible:ring-2 focus-visible:ring-white focus-visible:ring-offset-2 focus-visible:ring-offset-[#1e40af]" data-id="element-2417" data-mp="1jh8rsr8g8g0i">
            Get Matched Now
            <ArrowRightIcon className="h-4 w-4" aria-hidden="true" data-id="element-2418" data-mp="226p56f58g2yc" />
          </button>
        </div>
      </section>

      <footer className="bg-white px-4 py-8" data-id="element-2419" data-mp="lrxatwoj0lme">
        <div className="mx-auto max-w-4xl space-y-3 text-center text-xs leading-relaxed" data-id="element-2420" data-mp="qsbk1i1v61m0">
          <p className="text-gray-500" data-id="element-2421" data-mp="16zpt0bxm5r43">
            FindOnlineDegrees is an advertising-supported site. We may receive
            compensation when you request information from the schools featured
            here. Program availability varies by school; not every school offers
            every program at every degree level.
          </p>
          <TrackingNoticeLinks tone="light" data-id="element-2422" data-mp="sbuzitbs7ipy" />
        </div>
      </footer>
    </div>;
}